import React, { useEffect, useMemo, useState } from "react";
import { FaTimes, FaMedal } from "react-icons/fa";
import { getCombatsByCompetiteur } from "../../../services/combatsServices";

const Field = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm py-1">
    <span className="text-gray-600 dark:text-gray-300">{label}</span>
    <span className="font-semibold text-gray-900 dark:text-white">
      {value ?? "—"}
    </span>
  </div>
);

const resultLabel = (letter) => {
  if (!letter) return "—";
  const l = String(letter).trim().toUpperCase();
  if (l === "V") return "Gagné";
  if (l === "D") return "Perdu";
  if (l === "N") return "Nul";
  if (l === "F") return "Forfait";
  if (l === "H") return "Hansoku Make";
  return l;
};

const badgeClassesForResult = (res) => {
  switch (res) {
    case "Gagné":
      return "bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800";
    case "Perdu":
      return "bg-red-100 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-800";
    case "Nul":
      return "bg-yellow-100 text-yellow-700 ring-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:ring-yellow-800";
    case "Hansoku Make":
      return "bg-gray-900 text-gray-100 ring-gray-200 dark:bg-gray-900/30 dark:text-gray-100 dark:ring-gray-800";
    case "Forfait":
      return "bg-purple-100 text-purple-700 ring-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:ring-purple-800";
    default:
      return "bg-gray-100 text-gray-700 ring-gray-200 dark:bg-slate-800 dark:text-gray-300 dark:ring-slate-700";
  }
};

const PublicCompetitorSheet = ({
  competitor,
  onClose,
  compIndex,
  distById,
}) => {
  const [loading, setLoading] = useState(false);
  const [combats, setCombats] = useState([]);
  const [error, setError] = useState(null);

  // Récupération du grade du judoka sélectionné (depuis la ligne OU depuis l'index global)
  const selfFromIndex = compIndex?.get(Number(competitor?.id));
  const selfGrade =
    competitor?.grade_competiteur ?? selfFromIndex?.grade_competiteur ?? null;

  // Distribution principale du compétiteur (catégorie + genre)
  const mainDist =
    competitor?._distId != null
      ? distById?.get(Number(competitor._distId))
      : null;

  const mainGenreLabel =
    mainDist?.genre === "M"
      ? "Masculin"
      : mainDist?.genre === "F"
        ? "Féminin"
        : "";

  useEffect(() => {
    if (!competitor?.id) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await getCombatsByCompetiteur(competitor.id);
        setCombats(rows);
      } catch (e) {
        setError("Impossible de charger les combats.");
      } finally {
        setLoading(false);
      }
    })();
  }, [competitor?.id]);

  const computed = useMemo(() => {
    const selfId = Number(competitor?.id);
    if (!Number.isFinite(selfId))
      return { fights: 0, wins: null, points: null };

    let wins = 0;
    let points = 0;
    let fights = 0;

    (combats || []).forEach((f) => {
      const aId = Number(f.competiteur_id1);
      const bId = Number(f.competiteur_id2);
      const selfIsA = aId === selfId;
      const selfIsB = bId === selfId;
      if (!selfIsA && !selfIsB) return;

      fights += 1;

      const resLetter = selfIsA
        ? f.resultat_competiteur1
        : f.resultat_competiteur2;
      const myPts = selfIsA ? f.points_competiteur1 : f.points_competiteur2;
      const oppPts = selfIsA ? f.points_competiteur2 : f.points_competiteur1;

      // points
      const my = Number(myPts);
      if (Number.isFinite(my)) points += my;

      // victoire : lettre si dispo, sinon fallback points
      const L = String(resLetter || "")
        .trim()
        .toUpperCase();
      if (L === "V") wins += 1;
      else if (!L) {
        const op = Number(oppPts);
        if (Number.isFinite(my) && Number.isFinite(op) && my > op) wins += 1;
      }
    });

    return { fights, wins, points };
  }, [combats, competitor?.id]);

  if (!competitor) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex justify-end"
      onClick={onClose}
    >
      <aside
        className="w-full sm:w-[520px] h-full max-h-[100vh] overflow-y-auto bg-white dark:bg-slate-900 shadow-xl border-l border-gray-200 dark:border-slate-700 p-4 flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {competitor.nom_competiteur} {competitor.prenom_competiteur}
          </h3>
          <button
            onClick={onClose}
            className="rounded-md p-2 hover:bg-black/5 dark:hover:bg-white/10"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* club + grade du judoka sélectionné */}
        <div className="text-sm text-gray-600 dark:text-gray-300 mb-4 flex items-center gap-2 flex-wrap">
          <span>{competitor.club_competiteur}</span>
          {selfGrade && (
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 bg-slate-100 text-slate-700 ring-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
              {selfGrade}
            </span>
          )}
        </div>

        {/* Résumé */}
        <div className="rounded-xl border border-gray-200/70 dark:border-slate-700/70 p-3 mb-3">
          <Field
            label="Catégorie"
            value={
              competitor._age || competitor._poids || mainGenreLabel
                ? `${competitor._age || "—"}${
                    mainGenreLabel ? ` • ${mainGenreLabel}` : ""
                  } / ${competitor._poids || "—"}`
                : "—"
            }
          />
          <Field
            label="Victoires"
            value={
              computed.wins != null ? computed.wins : competitor.total_victoires
            }
          />
          <Field
            label="Points"
            value={
              computed.points != null
                ? computed.points
                : competitor.total_points
            }
          />
          <Field
            label="Classement"
            value={
              competitor.classement_position
                ? `${competitor.classement_position} ${
                    competitor.classement_position === 1
                      ? "🥇"
                      : competitor.classement_position === 2
                        ? "🥈"
                        : competitor.classement_position === 3
                          ? "🥉"
                          : ""
                  }`
                : "—"
            }
          />
        </div>

        {/* Répartition */}
        <div className="rounded-xl border border-gray-200/70 dark:border-slate-700/70 p-3 mb-3">
          <div className="font-semibold mb-2">Répartition</div>
          <Field
            label="Distribution"
            value={competitor._distId ? `#${competitor._distId}` : "—"}
          />
          <Field label="Tapis" value={competitor._tapis ?? "—"} />
          <Field label="Cat. âge" value={competitor._age} />
          <Field label="Cat. poids" value={competitor._poids} />
        </div>

        {/* Combats */}
        <div className="rounded-xl border border-gray-200/70 dark:border-slate-700/70 p-3 mb-3">
          <div className="font-semibold mb-2 flex items-center gap-2">
            <FaMedal /> Combats
          </div>

          {loading && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Chargement des combats…
            </div>
          )}
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {!loading && !error && combats.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Aucun combat trouvé pour ce judoka.
            </div>
          )}

          {!loading && !error && combats.length > 0 && (
            <ul className="space-y-2">
              {combats.map((f) => {
                const selfId = Number(competitor.id);
                const aId = Number(f.competiteur_id1);
                const bId = Number(f.competiteur_id2);
                const selfIsA = aId === selfId;
                const opponentId = selfIsA ? bId : aId;

                const opp = opponentId ? compIndex?.get(opponentId) : null;
                const opponentName = opp
                  ? `${opp.nom_competiteur} ${opp.prenom_competiteur}`
                  : `Adversaire #${opponentId || "?"}`;
                const opponentGrade = opp?.grade_competiteur || null;

                const dist = distById?.get(Number(f.distribution_id));
                const tapis = dist?.numero_tapis ?? null;
                const catAge = dist?.categorie_age ?? null;
                const catPoids = dist?.categorie_poids ?? null;

                const resLetter = selfIsA
                  ? f.resultat_competiteur1
                  : f.resultat_competiteur2;
                const res = resultLabel(resLetter);

                const myPts = selfIsA
                  ? f.points_competiteur1
                  : f.points_competiteur2;
                const oppPts = selfIsA
                  ? f.points_competiteur2
                  : f.points_competiteur1;

                return (
                  <li
                    key={f.id}
                    className="text-sm bg-gray-50 dark:bg-slate-800/60 rounded-md p-2"
                  >
                    {/* Titre : vs + grade + badge résultat */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold">
                        vs {opponentName}
                        {opponentGrade && (
                          <span className="ml-1 text-xs opacity-75">
                            ({opponentGrade})
                          </span>
                        )}
                        {tapis != null && (
                          <span className="ml-2 text-xs opacity-75">
                            — Tapis {tapis}
                          </span>
                        )}
                      </div>

                      <span
                        className={
                          "shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 " +
                          badgeClassesForResult(res)
                        }
                        title={`Résultat : ${res}`}
                      >
                        {res}
                      </span>
                    </div>

                    {(catAge || catPoids) && (
                      <div className="text-xs opacity-75 mt-0.5">
                        {catAge || "—"} • {catPoids || "—"}
                      </div>
                    )}

                    <div className="text-xs opacity-75 mt-0.5">
                      {myPts != null && oppPts != null
                        ? `Points ${myPts}–${oppPts}`
                        : "Points —"}
                    </div>

                    {f.details_combat && (
                      <div className="text-xs mt-1 bg-white/60 dark:bg-slate-900/50 rounded p-2 whitespace-pre-wrap">
                        {f.details_combat}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-auto text-xs text-gray-500 dark:text-gray-400">
          Astuce : utilisez la recherche en haut pour trouver rapidement un club
          ou un judoka, puis ouvrez sa fiche pour voir ses combats.
        </div>
      </aside>
    </div>
  );
};

export default PublicCompetitorSheet;
